import {
  resolveDomiAuthEnvironment,
  resolveDomiSsoCookieName,
  type DomiAuthEnvironment,
} from './domi-auth-environment';

export { resolveDomiAuthEnvironment, type DomiAuthEnvironment };

export const DOMI_TOKEN_KEY = 'domi_token';
export const DOMI_USER_KEY = 'domi_user';

/** 内置演示登录页（仅本地无 llm-agent 时的兜底）。 */
export const LOCAL_LOGIN_PATH = '/dev/login';
/** 测试站登录页（agent-base）。 */
const TEST_LOGIN_URL = 'https://agent-base.domi-kids.com/login';
/** 本地 llm-agent 登录页（与 appui-v2 `pnpm dev -p 3001` 对齐）。 */
const DEFAULT_LOCAL_DOMI_LOGIN_URL = 'http://127.0.0.1:3001/login';

export const DEMO_LOGIN_TOKEN = 'demo-local-token';
export const DEMO_LOGIN_USER_ID = '10086';

type LoginLocation = Pick<Location, 'origin' | 'hostname'>;

const DEFAULT_LOCAL_LOGIN_ORIGIN = 'http://127.0.0.1:5175';

function readPublicDomiLoginUrl(): string | null {
  if (typeof process === 'undefined') return null;
  const raw = process.env.NEXT_PUBLIC_OD_DOMI_LOGIN_URL?.trim();
  return raw || null;
}

function readLocalLoginOrigin(
  location: LoginLocation = typeof window !== 'undefined'
    ? window.location
    : { origin: DEFAULT_LOCAL_LOGIN_ORIGIN, hostname: '127.0.0.1' },
): string {
  if (location.origin && location.origin !== 'null') {
    return location.origin;
  }
  return DEFAULT_LOCAL_LOGIN_ORIGIN;
}

export type DomiUser = {
  id: string;
  nickname?: string;
  avatar?: string;
  phone?: string;
  phoneCode?: string;
  userIdentity?: string;
  [key: string]: unknown;
};

export const DOMI_SESSION_CHANGED_EVENT = 'domi-session-changed';

export class DomiUnauthorizedError extends Error {
  constructor(message = '请先登录') {
    super(message);
    this.name = 'DomiUnauthorizedError';
  }
}

export function isDomiUnauthorizedError(error: unknown): error is DomiUnauthorizedError {
  return error instanceof DomiUnauthorizedError;
}

const TENANT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function readDomiAuthOverride(): boolean | null {
  if (typeof process === 'undefined') return null;
  const raw = process.env.OD_REQUIRE_DOMI_AUTH?.trim().toLowerCase();
  if (raw === '1' || raw === 'true' || raw === 'yes') return true;
  if (raw === '0' || raw === 'false' || raw === 'no') return false;
  return null;
}

function hostnameFromLocation(location: Pick<Location, 'hostname'>): string {
  return location.hostname.trim().toLowerCase();
}

export function isDomiAuthRequired(
  location: Pick<Location, 'hostname'> = typeof window !== 'undefined'
    ? window.location
    : { hostname: '' },
): boolean {
  const override = readDomiAuthOverride();
  if (override != null) return override;
  return resolveDomiAuthEnvironment(hostnameFromLocation(location)) != null;
}

export function resolveLocalLoginUrl(
  location: LoginLocation = typeof window !== 'undefined'
    ? window.location
    : { origin: DEFAULT_LOCAL_LOGIN_ORIGIN, hostname: '127.0.0.1' },
): string {
  return `${readLocalLoginOrigin(location)}${LOCAL_LOGIN_PATH}`;
}

export function resolveDomiLoginUrl(
  environment: DomiAuthEnvironment = 'local',
  location?: LoginLocation,
): string {
  if (environment === 'prod') {
    const origin = location?.origin?.trim();
    // 生产环境登录必须与当前访问域同源，不能被构建时配置或域名别名改写。
    if (origin && origin !== 'null') return `${origin.replace(/\/$/, '')}/login`;
    return '/login';
  }

  // 本地和测试环境仍可显式使用跨域登录服务。
  const override = readPublicDomiLoginUrl();
  if (override) return override;
  if (environment === 'local') return DEFAULT_LOCAL_DOMI_LOGIN_URL;
  return TEST_LOGIN_URL;
}

export function resolveDomiLoginUrlForLocation(
  location: LoginLocation = typeof window !== 'undefined'
    ? window.location
    : { origin: DEFAULT_LOCAL_LOGIN_ORIGIN, hostname: '127.0.0.1' },
): string {
  const environment = resolveDomiAuthEnvironment(hostnameFromLocation(location));
  if (environment == null && readDomiAuthOverride() === true) {
    const origin = location.origin?.trim();
    if (origin && origin !== 'null') return `${origin.replace(/\/$/, '')}/login`;
  }
  return resolveDomiLoginUrl(environment ?? 'test', location);
}

export function buildDemoDomiSession(overrides?: {
  id?: string;
  nickname?: string;
}): { token: string; user: string } {
  const id = overrides?.id ?? DEMO_LOGIN_USER_ID;
  const nickname = overrides?.nickname ?? '演示用户';
  return {
    token: DEMO_LOGIN_TOKEN,
    user: JSON.stringify({ id, nickname }),
  };
}

export function seedDemoDomiSession(
  storage: Pick<Storage, 'setItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { setItem: () => undefined },
  overrides?: { id?: string; nickname?: string },
): void {
  const { token, user } = buildDemoDomiSession(overrides);
  storage.setItem(DOMI_TOKEN_KEY, token);
  storage.setItem(DOMI_USER_KEY, user);
}

export function hasDomiSession(
  storage: Pick<Storage, 'getItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null },
): boolean {
  const token = getDomiToken(storage);
  const user = storage.getItem(DOMI_USER_KEY)?.trim();
  return Boolean(token && (user || getSharedDomiToken()));
}

function readCookieValue(cookie: string, name: string): string | null {
  const prefix = `${name}=`;
  const entry = cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.slice(prefix.length)).trim() || null;
  } catch {
    return null;
  }
}

function sharedDomiCookieName(hostname: string): string {
  return resolveDomiSsoCookieName(hostname);
}

/** Domi Home writes a readable parent-domain SSO cookie for sibling applications. */
export function getSharedDomiToken(
  cookie: string = typeof document !== 'undefined' ? document.cookie : '',
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): string | null {
  if (!cookie || !hostname) return null;
  return readCookieValue(cookie, sharedDomiCookieName(hostname));
}

export function getDomiToken(
  storage: Pick<Storage, 'getItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null },
): string | null {
  const token = storage.getItem(DOMI_TOKEN_KEY)?.trim();
  return token || getSharedDomiToken();
}

export function getDomiUser(
  storage: Pick<Storage, 'getItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null },
): DomiUser | null {
  const tenantId = resolveDomiUserTenantId(storage);
  if (!tenantId) return null;

  const raw = storage.getItem(DOMI_USER_KEY)?.trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DomiUser;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ...parsed, id: tenantId };
    }
  } catch {
    // domi_user may be a bare user id string.
  }
  return { id: tenantId };
}

/**
 * Domi snowflake ids may be stored as JSON numbers that exceed
 * Number.MAX_SAFE_INTEGER. Prefer digit sequences from the raw text so
 * tenant folders match the daemon's Bearer → /v1/user/info identity.
 */
export function extractDomiUserIdFromRaw(bodyText: string): string | null {
  if (!bodyText.trim()) return null;
  for (const field of ['id', 'userId', 'user_id', 'uid']) {
    const match = bodyText.match(new RegExp(`"${field}"\\s*:\\s*"?([0-9A-Za-z_-]+)"?`));
    if (match?.[1]) return match[1];
  }
  return null;
}

/** Tenant folder name from localStorage `domi_user` (JSON object, JSON string, or bare id). */
export function resolveDomiUserTenantId(
  storage: Pick<Storage, 'getItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null },
): string | null {
  const raw = storage.getItem(DOMI_USER_KEY)?.trim();
  if (!raw) return null;

  // Raw extraction first: JSON.parse rounds snowflake numeric ids.
  const fromRaw = extractDomiUserIdFromRaw(raw);
  if (fromRaw && TENANT_ID_PATTERN.test(fromRaw)) return fromRaw;

  let candidate: string | null = null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const id = (parsed as { id?: unknown }).id;
      if (typeof id === 'string' && id.trim()) {
        candidate = id.trim();
      } else if (typeof id === 'number' && Number.isFinite(id)) {
        // Already-rounded Number — last resort only (lossy).
        candidate = String(id);
      }
    } else if (typeof parsed === 'string' && parsed.trim()) {
      candidate = parsed.trim();
    } else if (typeof parsed === 'number' && Number.isFinite(parsed)) {
      candidate = String(parsed);
    }
  } catch {
    candidate = raw;
  }

  if (!candidate || !TENANT_ID_PATTERN.test(candidate)) return null;
  return candidate;
}

function dispatchDomiSessionChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DOMI_SESSION_CHANGED_EVENT));
}

export function patchDomiUser(
  patch: Partial<DomiUser>,
  storage: Pick<Storage, 'getItem' | 'setItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null, setItem: () => undefined },
): void {
  const current = getDomiUser(storage);
  if (!current) return;
  const nextId =
    typeof patch.id === 'string' && patch.id.trim() && TENANT_ID_PATTERN.test(patch.id.trim())
      ? patch.id.trim()
      : current.id;
  const next: DomiUser = { ...current, ...patch, id: nextId };
  storage.setItem(DOMI_USER_KEY, JSON.stringify(next));
  dispatchDomiSessionChanged();
}

/** Rewrite stored `domi_user` so `id` is the exact snowflake string (not a rounded Number). */
export function normalizeStoredDomiUserId(
  storage: Pick<Storage, 'getItem' | 'setItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null, setItem: () => undefined },
): string | null {
  const raw = storage.getItem(DOMI_USER_KEY)?.trim();
  if (!raw) return null;
  const exactId = extractDomiUserIdFromRaw(raw);
  if (!exactId || !TENANT_ID_PATTERN.test(exactId)) return resolveDomiUserTenantId(storage);

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const next = { ...(parsed as DomiUser), id: exactId };
      const serialized = JSON.stringify(next);
      if (serialized !== raw) {
        storage.setItem(DOMI_USER_KEY, serialized);
        dispatchDomiSessionChanged();
      }
      return exactId;
    }
  } catch {
    // bare id or non-JSON — leave as-is when it already matches
  }
  return exactId;
}

export function clearDomiSession(
  storage: Pick<Storage, 'removeItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { removeItem: () => undefined },
): void {
  storage.removeItem(DOMI_TOKEN_KEY);
  storage.removeItem(DOMI_USER_KEY);
  dispatchDomiSessionChanged();
}

export function getDomiAuthorizationHeader(
  storage: Pick<Storage, 'getItem'> = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null },
): string | null {
  const token = getDomiToken(storage);
  if (!token) return null;
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function readLegacyAuthParams(location: Location): {
  token: string | null;
  user: string | null;
} {
  const searchParams = new URLSearchParams(location.search);
  let token = searchParams.get(DOMI_TOKEN_KEY)?.trim() || null;
  let user = searchParams.get(DOMI_USER_KEY)?.trim() || null;

  const rawHash = (location.hash || '').replace(/^#/, '');
  const hashQuery = rawHash.includes('?')
    ? rawHash.slice(rawHash.indexOf('?') + 1)
    : rawHash.includes('=')
      ? rawHash
      : '';
  if (hashQuery) {
    const hashParams = new URLSearchParams(hashQuery);
    token ||= hashParams.get(DOMI_TOKEN_KEY)?.trim() || null;
    user ||= hashParams.get(DOMI_USER_KEY)?.trim() || null;
  }
  return { token, user };
}

function stripAuthParamsFromUrl(
  location: Location,
  historyRef: Pick<History, 'replaceState'> | null,
  includeSsoCode = false,
): void {
  if (!historyRef) return;
  const names = includeSsoCode
    ? [DOMI_TOKEN_KEY, DOMI_USER_KEY, 'sso_code']
    : [DOMI_TOKEN_KEY, DOMI_USER_KEY];
  const searchParams = new URLSearchParams(location.search);
  for (const name of names) searchParams.delete(name);

  const rawHash = (location.hash || '').replace(/^#/, '');
  const separatorIndex = rawHash.indexOf('?');
  const hashPrefix = separatorIndex >= 0
    ? rawHash.slice(0, separatorIndex)
    : rawHash.includes('=')
      ? ''
      : rawHash;
  const hashQuery = separatorIndex >= 0
    ? rawHash.slice(separatorIndex + 1)
    : rawHash.includes('=')
      ? rawHash
      : '';
  const hashParams = new URLSearchParams(hashQuery);
  for (const name of names) hashParams.delete(name);
  const nextHashQuery = hashParams.toString();
  const nextHash = hashPrefix || nextHashQuery
    ? `#${hashPrefix}${nextHashQuery ? `${hashPrefix ? '?' : ''}${nextHashQuery}` : ''}`
    : '';
  const nextSearch = searchParams.toString();
  historyRef.replaceState(
    null,
    '',
    `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${nextHash}`,
  );
}

/** Accept the historical raw-token redirect only for rollout compatibility. */
export function absorbLegacyDomiAuthFromUrl(
  location: Location = typeof window !== 'undefined' ? window.location : ({} as Location),
  storage: Storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage),
  historyRef: Pick<History, 'replaceState'> | null =
    typeof window !== 'undefined' ? window.history : null,
  includeSsoCode = false,
): boolean {
  const { token, user } = readLegacyAuthParams(location);
  if (!token || !user) return false;

  storage.setItem(DOMI_TOKEN_KEY, token);
  storage.setItem(DOMI_USER_KEY, user);
  if (!normalizeStoredDomiUserId(storage)) {
    storage.removeItem(DOMI_TOKEN_KEY);
    storage.removeItem(DOMI_USER_KEY);
    return false;
  }
  dispatchDomiSessionChanged();
  stripAuthParamsFromUrl(location, historyRef, includeSsoCode);
  return true;
}

export async function exchangeDomiSsoCodeFromUrl(
  location: Location = typeof window !== 'undefined' ? window.location : ({} as Location),
  storage: Storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage),
  historyRef: Pick<History, 'replaceState'> | null =
    typeof window !== 'undefined' ? window.history : null,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('sso_code')?.trim();
  if (!code) return false;
  const response = await fetchImpl('/api/auth/sso/exchange', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
    cache: 'no-store',
  }).catch(() => null);
  if (!response?.ok) {
    return absorbLegacyDomiAuthFromUrl(location, storage, historyRef, true);
  }
  const payload = await response.json().catch(() => null) as {
    token?: unknown;
    user?: unknown;
  } | null;
  if (
    typeof payload?.token !== 'string'
    || !payload.token.trim()
    || !payload.user
    || typeof payload.user !== 'object'
    || Array.isArray(payload.user)
  ) return false;

  storage.setItem(DOMI_TOKEN_KEY, payload.token.trim());
  storage.setItem(DOMI_USER_KEY, JSON.stringify(payload.user));
  normalizeStoredDomiUserId(storage);
  dispatchDomiSessionChanged();

  if (historyRef) {
    searchParams.delete('sso_code');
    const nextSearch = searchParams.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || ''}`;
    historyRef.replaceState(null, '', nextUrl);
  }

  return true;
}

export function buildDomiLoginRedirectUrl(
  loginUrl: string,
  returnUrl: string,
): string {
  const url = new URL(loginUrl);
  url.searchParams.set('returnUrl', returnUrl);
  url.searchParams.set('ssoConsumer', 'open-design');
  return url.toString();
}

export function redirectToDomiLogin(
  returnUrl: string = typeof window !== 'undefined' ? window.location.href : '/',
): void {
  if (typeof window === 'undefined') return;
  const loginUrl = buildDomiLoginRedirectUrl(
    resolveDomiLoginUrlForLocation(),
    returnUrl,
  );
  window.location.assign(loginUrl);
}

export type EnsureDomiAuthResult = 'ok' | 'exchanging' | 'redirecting';

export function ensureDomiAuth(
  location: Location = typeof window !== 'undefined' ? window.location : ({} as Location),
  storage: Storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage),
  historyRef: Pick<History, 'replaceState'> | null =
    typeof window !== 'undefined' ? window.history : null,
): EnsureDomiAuthResult {
  if (!isDomiAuthRequired(location)) return 'ok';

  if (new URLSearchParams(location.search).get('sso_code')?.trim()) return 'exchanging';
  absorbLegacyDomiAuthFromUrl(location, storage, historyRef);
  if (hasDomiSession(storage)) return 'ok';

  const loginUrl = buildDomiLoginRedirectUrl(
    resolveDomiLoginUrlForLocation(location),
    location.href,
  );
  location.assign(loginUrl);
  return 'redirecting';
}
